import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Chip,
} from "@mui/material";

import Inventory2Icon from "@mui/icons-material/Inventory2";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import GroupIcon from "@mui/icons-material/Group";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PostAddIcon from "@mui/icons-material/PostAdd";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SearchIcon from "@mui/icons-material/Search";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import KitchenIcon from "@mui/icons-material/Kitchen";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import SubscriptionStatus from "../common/SubscriptionStatus";
import { userHasActiveEntitlement } from "../../utils/entitlements";
import api from "../../services/api";

const UserDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const match = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
  const prefix = match ? `/${match[1]}` : "";

  // ✅ προσαρμόζεις εδώ αν έχεις συγκεκριμένο πεδίο premium
  const isPremium = useMemo(() => userHasActiveEntitlement(user), [user]);

  const [authOpen, setAuthOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [blockedCard, setBlockedCard] = useState(null);
  const [watchSummary, setWatchSummary] = useState({
    loading: false,
    followedCount: 0,
    recentChanges: 0,
  });

  const loginPath = `${prefix}/login`;
  const registerPath = `${prefix}/register`;
  const pricingPath = `${prefix}/plans`;
  const displayName = (user?.username || "").trim();
  const welcomeTitle = user
    ? `${t("welcome_to_your_dashboard") || "Welcome to your dashboard"}, ${displayName || t("account") || "Account"}!`
    : (t("welcome_to_incooking") || "Welcome to InCooking");

  useEffect(() => {
    let active = true;
    if (!user || !isPremium) {
      setWatchSummary({ loading: false, followedCount: 0, recentChanges: 0 });
      return undefined;
    }
    setWatchSummary((prev) => ({ ...prev, loading: true }));
    api.followedGeneric
      .list()
      .then((res) => {
        if (!active) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        const now = Date.now();
        const recentChanges = items.filter((item) => {
          const ts = item?.price_changed_at ? new Date(item.price_changed_at).getTime() : 0;
          return ts > 0 && now - ts <= 7 * 24 * 60 * 60 * 1000;
        }).length;
        setWatchSummary({
          loading: false,
          followedCount: items.length,
          recentChanges,
        });
      })
      .catch(() => {
        if (!active) return;
        setWatchSummary({ loading: false, followedCount: 0, recentChanges: 0 });
      });
    return () => {
      active = false;
    };
  }, [user, isPremium]);

  const cards = [
    {
      icon: <KitchenIcon fontSize="large" color="primary" />,
      titleKey: "smart_kitchen.nav_card_title",
      descKey: "smart_kitchen.nav_card_desc",
      btnKey: "smart_kitchen.nav_card_cta",
      color: "primary",
      path: `${prefix}/smart-kitchen`,
      requiresAuth: false,
      positioningFeature: "smart-kitchen",
    },
    {
      icon: <GroupIcon fontSize="large" color="primary" />,
      titleKey: "household_profile",
      descKey: "update_your_household_details_and_preferences",
      btnKey: "manage_profile",
      color: "primary",
      path: `${prefix}/client/household/profile`,
      requiresAuth: false,
    },
    {
      icon: <RestaurantIcon fontSize="large" color="warning" />,
      titleKey: "meal_plan",
      descKey: "generate_meal_plan",
      btnKey: "view_meal_plan",
      color: "warning",
      path: `${prefix}/client/meal/plan`,
      requiresAuth: false,
    },
    {
      icon: <ShoppingCartIcon fontSize="large" color="error" />,
      titleKey: "shopping_list",
      descKey: "view_and_manage_your_shopping_list",
      btnKey: "view_shopping_list",
      color: "error",
      path: `${prefix}/client/select-shopping-list`,
      requiresAuth: false,
    },
    {
      icon: <PriceChangeIcon fontSize="large" color="success" />,
      titleKey: "price_watch_title",
      descKey: "track_variant_prices_and_alerts",
      btnKey: "open_watchlist",
      color: "success",
      path: `${prefix}/price-watch`,
      requiresAuth: true,
      requiresPremium: true,
      premiumFeature: "price-watch",
    },

    // ✅ ΝΕΟ: Check inventory (μόνο για συνδεμένους – και premium αν θες)
    {
      icon: <Inventory2Icon fontSize="large" color="primary" />,
      titleKey: "check_inventory",
      descKey: "review_and_manage_your_current_inventory",
      btnKey: "check_inventory",
      color: "primary",
      path: `${prefix}/client/inventory/check`,
      requiresAuth: true,
      requiresPremium: false, // κάν’ το true αν θες να είναι Premium-only
    },

    // ✅ Add inventory (συνδεμένοι - και μπορείς να το κάνεις premium-only)
    {
      icon: <AddCircleIcon fontSize="large" color="secondary" />,
      titleKey: "add_product_inventory",
      descKey: "update_your_inventory_with_new_products",
      btnKey: "add_inventory",
      color: "secondary",
      path: `${prefix}/client/inventory/add/product/inventory`,
      requiresAuth: true,
      requiresPremium: true, // 👈 προτείνω να είναι Premium. Αν θες free με όριο, κάν’ το false.
    },

    // ✅ My recipes (μόνο συνδεμένοι)
    {
      icon: <MenuBookIcon fontSize="large" color="success" />,
      titleKey: "my_recipes",
      descKey: "view_and_manage_your_personal_and_public_recipes",
      btnKey: "view_recipes",
      color: "success",
      path: `${prefix}/client/recipes-user`,
      requiresAuth: true,
    },

    // ✅ Add recipe (μόνο συνδεμένοι, προτείνω free)
    {
      icon: <PostAddIcon fontSize="large" color="info" />,
      titleKey: "add_recipe",
      descKey: "add_your_own_recipe_and_share_with_the_community",
      btnKey: "add_recipe",
      color: "info",
      path: `${prefix}/client/add-recipe`,
      requiresAuth: true,
    },

    // αυτά μπορούν να είναι και για guest αν θες (βάζω requiresAuth: false)
    {
      icon: <FlashOnIcon fontSize="large" color="warning" />,
      titleKey: "quick_recipe",
      descKey: "find_recipe_with_one_ingredient",
      btnKey: "quick_recipe",
      color: "warning",
      path: `${prefix}/quick-recipe`,
      requiresAuth: false,
    },
    {
      icon: <SearchIcon fontSize="large" color="info" />,
      titleKey: "search_recipes",
      descKey: "search_recipes_with_filters",
      btnKey: "search_recipes",
      color: "info",
      path: `${prefix}/search-recipes`,
      requiresAuth: false,
    },
    {
      icon: <SavingsOutlinedIcon fontSize="large" color="success" />,
      titleKey: "budget_recipes.dashboard_title",
      descKey: "budget_recipes.dashboard_desc",
      btnKey: "budget_recipes.dashboard_cta",
      color: "success",
      path: `${prefix}/budget-recipes`,
      requiresAuth: false,
    },
  ];

  const handleCardNavigate = (card) => {
    // 1) auth gate
    if (card.requiresAuth && !user) {
      setBlockedCard(card);
      setAuthOpen(true);
      return;
    }

    // 2) premium gate
    if (card.requiresPremium && !isPremium) {
      setBlockedCard(card);
      setPremiumOpen(true);
      return;
    }

    // 3) ok
    navigate(card.path);
  };

  return (
    <Container
      sx={{
        mt: { xs: 1, sm: 4 },
        mb: 4,
        py: { xs: 1, sm: 3 },
        borderRadius: 5,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 32px #161a20cc"
            : "0 8px 38px #c5e5d233",
        bgcolor: theme.palette.background.paper,
        minHeight: { xs: 0, sm: 600 },
        transition: "background 0.25s",
      }}
    >
      <Typography
        variant={isMobile ? "h5" : "h4"}
        gutterBottom
        sx={{
          textAlign: "center",
          fontWeight: 800,
          letterSpacing: "0.03em",
          color: theme.palette.primary.main,
          mb: { xs: 1, sm: 3 },
        }}
      >
        {welcomeTitle}
      </Typography>

      <Box
        id="dashboard-premium-widget"
        data-testid="dashboard-premium-widget"
        sx={{ mb: { xs: 2, sm: 4 }, mx: "auto", maxWidth: 500 }}
      >
        <SubscriptionStatus user={user} />
      </Box>

      <Grid container spacing={isMobile ? 2 : 3}>
        {cards.map((card) => {
          const locked = (card.requiresAuth && !user) || (card.requiresPremium && !isPremium);

          return (
            <Grid item xs={6} sm={6} md={4} key={card.path}>
              <Card
                id={card.path.endsWith("/client/meal/plan") ? "dashboard-meal-plan-entry" : undefined}
                data-testid={
                  card.positioningFeature === "smart-kitchen"
                    ? "dashboard-smart-kitchen-entry"
                    : card.path.endsWith("/client/meal/plan")
                      ? "dashboard-meal-plan-entry"
                      : undefined
                }
                onClick={() => handleCardNavigate(card)}
                sx={{
                  cursor: "pointer",
                  height: "100%",
                  textAlign: "center",
                  borderRadius: 4,
                  boxShadow: 3,
                  p: 2,
                  transition: "transform 0.17s, box-shadow 0.17s, opacity 0.17s",
                  "&:hover": {
                    transform: "translateY(-7px) scale(1.035)",
                    boxShadow: 10,
                    zIndex: 2,
                  },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: isMobile ? 150 : 260,
                  bgcolor: theme.palette.background.default,
                  opacity: locked ? 0.92 : 1,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                    {card.icon}
                  </Box>

                  {/* μικρό badge lock/premium */}
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                    {card.requiresPremium && !isPremium ? (
                      <Chip
                        size="small"
                        icon={<WorkspacePremiumRoundedIcon />}
                        label={t("premium_required") || "Premium"}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : card.requiresAuth && !user ? (
                      <Chip
                        size="small"
                        icon={<LockRoundedIcon />}
                        label={t("login_required") || "Login required"}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : null}
                  </Box>

                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    {t(card.titleKey)}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      minHeight: 38,
                      fontSize: { xs: "0.97rem", sm: "1.04rem" },
                    }}
                  >
                    {card.premiumFeature === "price-watch" && isPremium
                      ? (watchSummary.loading
                        ? (t("loading") || "Loading...")
                        : `${t("followed_products_count", { count: watchSummary.followedCount }) || `${watchSummary.followedCount} followed`} • ${watchSummary.recentChanges} ${t("recent_price_changes") || "recent changes"}`)
                      : card.premiumFeature === "price-watch" && !isPremium
                        ? (t("price_watch_dashboard_locked") || "Follow products, spot drops faster, and keep your best offers in one Premium view.")
                        : t(card.descKey)}
                  </Typography>

                  {card.premiumFeature === "price-watch" ? (
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 1 }}>
                      {isPremium ? (
                        <>
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${watchSummary.followedCount} ${t("followed_items") || "followed"}`}
                          />
                          <Chip
                            size="small"
                            color={watchSummary.recentChanges > 0 ? "warning" : "default"}
                            variant="outlined"
                            label={`${watchSummary.recentChanges} ${t("recent_changes") || "recent changes"}`}
                          />
                        </>
                      ) : (
                        <Chip
                          size="small"
                          icon={<WorkspacePremiumRoundedIcon />}
                          color="warning"
                          variant="outlined"
                          label={t("premium_required") || "Premium"}
                        />
                      )}
                    </Stack>
                  ) : null}
                </CardContent>

                <Button
                  variant="contained"
                  color={card.color}
                  sx={{
                    mt: 2,
                    px: 3,
                    py: 1.2,
                    fontWeight: 800,
                    borderRadius: 3,
                    letterSpacing: 0.5,
                    fontSize: "1rem",
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // για να μη διπλο-τρέξει με το onClick του Card
                    handleCardNavigate(card);
                  }}
                >
                  {t(card.btnKey)}
                </Button>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* =========================
          AUTH REQUIRED MODAL
         ========================= */}
      <Dialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 0.5,
            boxShadow: theme.palette.mode === "dark" ? 24 : 12,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
              }}
            >
              <LockRoundedIcon />
            </Box>
            <Box>
              <Typography fontWeight={900} fontSize="1.1rem">
                {t("login_required_title") || "Συνδέσου για να συνεχίσεις"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {blockedCard ? t(blockedCard.titleKey) : ""}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t("login_required_desc") ||
              "Η αποθήκη και οι προσωπικές λειτουργίες είναι διαθέσιμες μόνο σε συνδεμένους χρήστες."}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {t("benefits") || "Με σύνδεση παίρνεις:"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("benefit_save") || "Αποθήκευση επιλογών & δεδομένων"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("benefit_sync") || "Συγχρονισμό σε όλες τις συσκευές"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("benefit_personal") || "Προσωπικές συνταγές & αποθήκη"}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1.2 }}>
          <Button
            onClick={() => setAuthOpen(false)}
            variant="text"
            sx={{ fontWeight: 800 }}
          >
            {t("not_now") || "Όχι τώρα"}
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={() => {
              setAuthOpen(false);
              navigate(registerPath);
            }}
            variant="outlined"
            sx={{ fontWeight: 900, borderRadius: 3 }}
          >
            {t("create_account") || "Εγγραφή"}
          </Button>

          <Button
            onClick={() => {
              setAuthOpen(false);
              navigate(loginPath);
            }}
            variant="contained"
            sx={{ fontWeight: 900, borderRadius: 3 }}
          >
            {t("login") || "Σύνδεση"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =========================
          PREMIUM REQUIRED MODAL
         ========================= */}
      <Dialog
        open={premiumOpen}
        onClose={() => setPremiumOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 0.5,
            boxShadow: theme.palette.mode === "dark" ? 24 : 12,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
              }}
            >
              <WorkspacePremiumRoundedIcon />
            </Box>
            <Box>
              <Typography fontWeight={900} fontSize="1.1rem">
                {t("premium_required_title") || "Απαιτείται Premium"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {blockedCard ? t(blockedCard.titleKey) : ""}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t("premium_required_desc") ||
              "Αυτή η λειτουργία είναι διαθέσιμη στο Premium για καλύτερη εμπειρία και προηγμένες δυνατότητες."}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {t("premium_includes") || "Το Premium περιλαμβάνει:"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("premium_unlimited_inventory") || "Απεριόριστη αποθήκη"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("premium_smart_features") || "Έξυπνους υπολογισμούς & αυτοματισμούς"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {t("premium_priority") || "Προτεραιότητα σε νέες λειτουργίες"}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1.2 }}>
          <Button
            onClick={() => setPremiumOpen(false)}
            variant="text"
            sx={{ fontWeight: 800 }}
          >
            {t("not_now") || "Όχι τώρα"}
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={() => {
              setPremiumOpen(false);
              navigate(pricingPath);
            }}
            variant="contained"
            sx={{ fontWeight: 900, borderRadius: 3 }}
          >
            {t("view_premium") || "Δες Premium"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDashboard;
